import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";

import { useUpdateProductVisibilityMutation } from "@dashboard/products/api";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { MultipleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showSuccessToast } from "@shared/toasts/Toasts";

const visibilityOptions = [
    { value: "ExportableBuilds", label: i18next.t("pages:adminProductsPage.visibility.exportableBuilds") },
    { value: "PublicInheritance", label: i18next.t("pages:adminProductsPage.visibility.publicInheritance") },
    { value: "PublicFirmware", label: i18next.t("pages:adminProductsPage.visibility.publicFirmware") },
    { value: "PublicDeviceRegistration", label: i18next.t("pages:adminProductsPage.visibility.publicDeviceRegistration") },
];

export type VisibilityCellProps = { product: API.Product };

export const VisibilityCell = (props: VisibilityCellProps) => {
    const { t } = useTranslation(["pages"]);

    // Read straight from the product rather than seeding local state from it: the mutation invalidates "Products",
    // so the refetched list is the source of truth. Local state used to survive that refetch, and because rows are
    // identified by position, a deleted product shifted every row up and left each select showing the previous
    // occupant's flags — with the next change then saved against the product actually rendered there.
    const selectedValues = props.product.visibility ?? [];

    const [updateProductVisibility] = useUpdateProductVisibilityMutation();

    const handleVisibilityChange: MultipleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        const selectedValuesToSet = selectedOption.map((option) => option.value.toString());
        const result = await updateProductVisibility({ productId: props.product.id, visibility: selectedValuesToSet });
        if (!("error" in result)) {
            showSuccessToast(t("pages:adminProductsPage.toast.visibilityUpdatedSuccess"));
        }
    };

    return (
        <MinimalTableCell>
            <SelectField
                options={visibilityOptions}
                value={visibilityOptions.filter((option) => selectedValues.includes(option.value))}
                onChange={handleVisibilityChange}
                fullWidth
                multiple
            />
        </MinimalTableCell>
    );
};
